import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import {
  FiCheckCircle,
  FiClock,
  FiExternalLink,
  FiMenu,
  FiTrash2,
  FiUploadCloud,
  FiX,
  FiXCircle,
} from 'react-icons/fi';
import * as S from './styles';
import Sidebar from '../../../components/Sidebar';
import { useAuth } from '../../../hooks/AuthContext';
import { useToast } from '../../../hooks/toast';
import {
  ALLOWED_MIME_TYPES,
  CaregiverDocument,
  CaregiverDocumentsResponse,
  DocumentStatus,
  DocumentType,
  MAX_CERTIFICATES,
  MAX_FILE_SIZE_IN_MB,
  deleteDocument,
  listDocuments,
  uploadDocuments,
  validateDocumentFile,
} from '../../../services/documents';

interface DocumentSlot {
  type: DocumentType;
  title: string;
  description: string;
}

const SINGLE_FILE_SLOTS: DocumentSlot[] = [
  {
    type: 'document_front',
    title: 'Documento com foto (frente)',
    description: 'RG ou CNH. Imagem ou PDF.',
  },
  {
    type: 'document_back',
    title: 'Documento com foto (verso)',
    description: 'RG ou CNH. Imagem ou PDF.',
  },
  {
    type: 'resume',
    title: 'Currículo',
    description: 'PDF, Word ou imagem.',
  },
];

const STATUS_LABEL: Record<DocumentStatus, string> = {
  pending: 'Em análise',
  approved: 'Aprovado',
  rejected: 'Recusado',
};

const STATUS_ICON: Record<DocumentStatus, JSX.Element> = {
  pending: <FiClock />,
  approved: <FiCheckCircle />,
  rejected: <FiXCircle />,
};

function getErrorMessage(err: any): string {
  return (
    err?.response?.data?.message ||
    'Não foi possível concluir a ação. Tente novamente.'
  );
}

const Documents: React.FC = () => {
  const { user } = useAuth();
  const { addToast } = useToast();

  const [isOpen, setOpen] = useState(true);
  const [data, setData] = useState<CaregiverDocumentsResponse | null>(null);
  const [busyType, setBusyType] = useState<DocumentType | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const hasFormation = String(user.formation) === 'true';

  const loadDocuments = useCallback(async () => {
    try {
      setData(await listDocuments());
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro ao carregar documentos',
        description: getErrorMessage(err),
      });
    }
  }, [addToast]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleUpload = useCallback(
    async (type: DocumentType, e: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      // Reset so picking the same file again still triggers onChange
      e.target.value = '';

      if (files.length === 0) return;

      const invalid = files
        .map(file => validateDocumentFile(type, file))
        .find(Boolean);

      if (invalid) {
        addToast({
          type: 'error',
          title: 'Arquivo inválido',
          description: invalid,
        });
        return;
      }

      if (type === 'certificate') {
        const sent = (data?.documents || []).filter(
          document => document.type === 'certificate',
        ).length;

        if (sent + files.length > MAX_CERTIFICATES) {
          addToast({
            type: 'error',
            title: 'Limite de certificados',
            description: `Você pode enviar no máximo ${MAX_CERTIFICATES} certificados.`,
          });
          return;
        }
      }

      setBusyType(type);

      try {
        setData(await uploadDocuments(type, files));
        addToast({
          type: 'success',
          title: 'Documento enviado!',
          description: 'Ele será analisado pela equipe Cuid.',
        });
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Erro ao enviar documento',
          description: getErrorMessage(err),
        });
      } finally {
        setBusyType(null);
      }
    },
    [addToast, data],
  );

  const handleDelete = useCallback(
    async (document: CaregiverDocument) => {
      setBusyId(document.id);

      try {
        await deleteDocument(document.id);
        await loadDocuments();
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Erro ao remover documento',
          description: getErrorMessage(err),
        });
      } finally {
        setBusyId(null);
      }
    },
    [addToast, loadDocuments],
  );

  // Signed URLs expire after 5 minutes, so fetch a fresh one on every open
  const handleOpen = useCallback(
    async (document: CaregiverDocument) => {
      const popup = window.open('', '_blank');

      try {
        const fresh = await listDocuments();
        setData(fresh);

        const url = fresh.documents.find(item => item.id === document.id)?.url;

        if (url && popup) {
          popup.location.href = url;
        } else {
          popup?.close();
        }
      } catch (err) {
        popup?.close();
        addToast({
          type: 'error',
          title: 'Erro ao abrir documento',
          description: getErrorMessage(err),
        });
      }
    },
    [addToast],
  );

  function renderFile(document: CaregiverDocument) {
    return (
      <S.FileRow key={document.id}>
        <S.Status status={document.status}>
          {STATUS_ICON[document.status]}
          {STATUS_LABEL[document.status]}
        </S.Status>

        <span className="file-name">
          {document.original_name || 'Arquivo enviado'}
        </span>

        {document.status === 'rejected' && document.rejection_reason && (
          <p className="rejection">Motivo: {document.rejection_reason}</p>
        )}

        <div className="file-actions">
          {document.url && (
            <button type="button" onClick={() => handleOpen(document)}>
              <FiExternalLink />
              Ver
            </button>
          )}

          {document.status !== 'approved' && (
            <button
              type="button"
              disabled={busyId === document.id}
              onClick={() => handleDelete(document)}
            >
              <FiTrash2 />
              Remover
            </button>
          )}
        </div>
      </S.FileRow>
    );
  }

  function renderUploadButton(
    type: DocumentType,
    label: string,
    multiple = false,
  ) {
    const isBusy = busyType === type;

    return (
      <S.UploadButton disabled={isBusy}>
        <FiUploadCloud />
        {isBusy ? 'Enviando...' : label}
        <input
          type="file"
          accept={ALLOWED_MIME_TYPES[type].join(',')}
          multiple={multiple}
          disabled={isBusy}
          onChange={e => handleUpload(type, e)}
        />
      </S.UploadButton>
    );
  }

  const documents = data?.documents || [];
  const certificates = documents.filter(
    document => document.type === 'certificate',
  );

  // Computed here because the backend still counts profile_photo as required
  const missingCount =
    SINGLE_FILE_SLOTS.filter(
      slot => !documents.some(document => document.type === slot.type),
    ).length + (hasFormation && certificates.length === 0 ? 1 : 0);

  return (
    <S.Container>
      <div className="top-menu" onClick={() => setOpen(!isOpen)}>
        {isOpen ? <FiX /> : <FiMenu />}
      </div>
      {isOpen ? <Sidebar /> : ''}

      <S.Main>
        <S.Header>
          <h2>Documentação</h2>
          <p>
            Envie seus documentos para verificarmos seu perfil. Arquivos de até{' '}
            {MAX_FILE_SIZE_IN_MB}MB.
          </p>
          {data && missingCount > 0 && (
            <strong className="missing">
              {missingCount === 1
                ? 'Falta 1 documento'
                : `Faltam ${missingCount} documentos`}
            </strong>
          )}
        </S.Header>

        {!data ? (
          <S.Card>
            <p>Carregando...</p>
          </S.Card>
        ) : (
          <>
            {SINGLE_FILE_SLOTS.map(slot => {
              const current = documents.find(
                document => document.type === slot.type,
              );

              return (
                <S.Card key={slot.type}>
                  <div className="card-header">
                    <strong>{slot.title}</strong>
                    <p>{slot.description}</p>
                  </div>

                  {current && renderFile(current)}

                  {current?.status !== 'approved' &&
                    renderUploadButton(
                      slot.type,
                      current ? 'Enviar novo arquivo' : 'Enviar arquivo',
                    )}
                </S.Card>
              );
            })}

            <S.Card>
              <div className="card-header">
                <strong>Certificados {hasFormation ? '' : '(opcional)'}</strong>
                <p>
                  Cursos e formações na área. Imagem ou PDF, até{' '}
                  {MAX_CERTIFICATES} arquivos.
                </p>
              </div>

              {certificates.map(document => renderFile(document))}

              {certificates.length < MAX_CERTIFICATES &&
                renderUploadButton(
                  'certificate',
                  'Adicionar certificado',
                  true,
                )}
            </S.Card>
          </>
        )}
      </S.Main>
    </S.Container>
  );
};

export default Documents;
